import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FilterType } from '@/models';
import { FilterState } from '@/types/filter';
import { useFiltersContext } from '@/context/FiltersContext/FiltersContextProvider';
import { getActiveTotalCount } from '@/utils/filter/cascadingFilters';

export const useFilterCounts = () => {
  const { getValues, control } = useFormContext<FilterState>();
  const { availableValuesByFilter = {} } = useFiltersContext();
  useWatch({ control });

  const hasFilterValue = useCallback(
    (key: FilterType): boolean => {
      const value = getValues(key);
      if (value == null) return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'string') return value.trim().length > 0;
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value > 0;
      return false;
    },
    [getValues],
  );

  const getActiveFiltersCount = useCallback(
    (filterKeys: (FilterType | FilterType[])[]) =>
      filterKeys.filter(key =>
        Array.isArray(key) ? key.some(filterKey => hasFilterValue(filterKey)) : hasFilterValue(key),
      ).length,
    [hasFilterValue],
  );

  const getTotalSelectedItemsCount = useCallback(
    (filterKeys: (FilterType | FilterType[])[]) => {
      return filterKeys
        .flatMap(key => (Array.isArray(key) ? key : [key]))
        .reduce((total, key) => {
          const value = getValues(key);
          if (Array.isArray(value)) return total + value.length;
          if (value === true) return total + 1;
          if (typeof value === 'number' && value > 0) return total + 1;
          return total;
        }, 0);
    },
    [getValues],
  );

  const getSelectedItemsCountSplit = useCallback(
    (filterKeys: (FilterType | FilterType[])[]) => {
      return filterKeys
        .flatMap(key => (Array.isArray(key) ? key : [key]))
        .reduce(
          (totals, key) => {
            const value = getValues(key);
            if (Array.isArray(value)) {
              const availableValues = availableValuesByFilter[key];
              if (availableValues) {
                const { active, total } = getActiveTotalCount(value, availableValues);
                return { active: totals.active + active, total: totals.total + total };
              }
              return { active: totals.active + value.length, total: totals.total + value.length };
            }
            if (value === true || (typeof value === 'number' && value > 0)) {
              return { active: totals.active + 1, total: totals.total + 1 };
            }
            return totals;
          },
          { active: 0, total: 0 },
        );
    },
    [getValues, availableValuesByFilter],
  );

  return {
    getActiveFiltersCount,
    getTotalSelectedItemsCount,
    getSelectedItemsCountSplit,
  };
};
